import type { EnumItemExtension } from 'enum-plus/extension';
import type { LocalizeTemplate, LocalizeTemplatesConfig } from './auto-localize';
import { getTemplateFields, isAutoLocalizeMetaField, resolveLocalizeTemplate } from './auto-localize';
import type { EnumItemFields } from './enum-items';
import { internalConfig, localizer } from './global-config';
import type {
  EnumInit,
  EnumItemInit,
  EnumItemLabel,
  EnumKey,
  EnumValue,
  LocalizeInterface,
  ValueTypeFromSingleInit,
} from './types';
import { IS_ENUM_ITEM } from './utils';

export type EnumItemInterface<
  ET extends EnumInit<K, V>,
  T extends EnumItemInit<V>,
  K extends EnumKey<ET> = EnumKey<ET>,
  V extends EnumValue = ValueTypeFromSingleInit<T, K>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  LP = any,
  OPTIONS extends EnumItemOptions<ET, T, K, V, LP> = EnumItemOptions<ET, T, K, V, LP>,
> = EnumItemClass<ET, T, K, V, LP, OPTIONS> & {
  [key in Exclude<keyof T, 'value' | 'label' | 'key'>]: T[key];
} & (OPTIONS extends { templates: { items: infer ItemTemplates } }
    ? { readonly [key in keyof ItemTemplates]: string }
    : unknown) &
  EnumItemExtension<ET, K, V>;

/**
 * - **EN:** Represents a single item in an enumeration collection.
 * - **CN:** 表示枚举集合中的单个枚举项
 *
 * @template T Represents the type of the enum item's initialization object | 表示枚举项初始化对象的类型
 * @template V Represents the type of the enum item's value (usually string or number) |
 *   表示枚举项值的类型（通常是字符串或数字）
 * @template K Represents the type of the enum item's key | 表示枚举项键的类型
 * @template LP Represents the type of the enum item's label prefix | 表示枚举项标签的前缀值
 */
export class EnumItemClass<
  const ET extends EnumInit<K, V>,
  const T extends EnumItemInit<V>,
  K extends EnumKey<ET> = EnumKey<ET>,
  V extends EnumValue = ValueTypeFromSingleInit<T, K>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const LP = any,
  OPTIONS extends EnumItemOptions<ET, T, K, V, LP> = EnumItemOptions<ET, T, K, V, LP>,
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _$options?: EnumItemOptions<any, any, any, any, any>;

  /**
   * - **EN:** Creates an instance of EnumItemClass.
   * - **CN:** 创建 EnumItemClass 的实例
   *
   * @param key The key of the enum item | 枚举项键
   * @param value The value of the enum item | 枚举项值
   * @param label The display name of the enum item | 枚举项显示名称
   * @param raw The original initialization object | 原始初始化对象
   * @param options Optional settings for the enum item | 枚举项的可选设置
   */
  constructor(key: K, value: V, label: EnumItemLabel, raw: T, options?: OPTIONS) {
    this.key = key;
    this.value = value;
    this.label = label as string;
    this.raw = raw;
    const define = Object.defineProperty;
    // Do not use instance field here, because don't want print this field in Node.js
    define(this, '_$options', { value: options });

    // Add meta fields.
    // Additionally, if auto localize meta or templates.items declare extended fields,
    // they will be automatically added to the meta collection after internationalization.
    const autoLocalizeGetters: PropertyDescriptorMap = {};
    const rawMetaKeys = metaKeys(raw);
    const templateKeys = getTemplateFields(options);
    const metaKeyList = Array.from(new Set([...rawMetaKeys, ...templateKeys])).filter((metaKey) => !(metaKey in this));
    metaKeyList.forEach((metaKey) => {
      if (isAutoLocalizeMetaField(metaKey, options)) {
        autoLocalizeGetters[metaKey] = {
          get(this: EnumItemClass<ET, T, K, V, LP, OPTIONS>) {
            return this._$localizeResource(this.raw?.[metaKey as never], metaKey);
          },
          enumerable: true,
        };
      } else {
        this[metaKey as keyof this] = raw![metaKey as never] as this[keyof this];
      }
    });

    // Define getters to localize i18n key into localized text
    Object.defineProperties(this, {
      ...autoLocalizeGetters,
      label: {
        get(this: EnumItemClass<ET, T, K, V, LP, OPTIONS>) {
          return this._$localizeResource(label, 'label');
        },
        enumerable: true,
      },
    });

    const freeze = Object.freeze;
    const proto = EnumItemClass.prototype;
    freeze(this);
    if (!Object.isFrozen(proto)) {
      freeze(proto);
    }
  }

  /**
   * - **EN:** The value of the enum item
   * - **CN:** 枚举项的值
   */
  readonly value: V;

  /**
   * - **EN:** The label of the enum item (also known as display name)
   * - **CN:** 枚举项的标签（亦称显示名称）
   */
  readonly label: string;

  /**
   * - **EN:** The key of the enum item, which is the key in the initialization object when creating
   *   the enum collection.
   * - **CN:** 枚举项的键，即创建枚举集合时初始化对象中的键
   */
  readonly key: K;

  /**
   * - **EN:** The original initialization object of the enum item, which is the sub-object of a
   *   single enum item when creating the enum collection.
   * - **CN:** 枚举项的原始初始化对象，即创建枚举集合时单个枚举项的子对象
   */
  readonly raw: T;
  /**
   * - **EN:** A boolean value indicates that this is an enum item instance.
   * - **CN:** 布尔值，表示这是一个枚举项实例
   */
  // Do not use readonly field here, because don't want print this field in Node.js
  // eslint-disable-next-line @typescript-eslint/class-literal-property-style
  get [IS_ENUM_ITEM](): true {
    return true;
  }
  /**
   * - **EN:** Auto convert to a correct primitive type. This method is called when the object is used
   *   in a context that requires a primitive value.
   *
   * > The priority of this method is higher than both `valueOf` and `toString` methods.
   *
   * - **CN:** 自动转换为正确的原始类型。当对象被用在需要原始值的上下文中时会调用此方法。
   *
   * > 此方法的优先级高于 `valueOf` 和 `toString` 方法。
   *
   * @param hint {'number' | 'string' | 'default'} - A string indicating the preferred type of the
   *   result | 指示结果的首选类型
   *
   * @returns The primitive value of the enum item, either its value or label based on the hint |
   *   枚举项的原始值，根据提示返回其值或标签
   */
  // @ts-expect-error: because don't want show `Symbol` in vscode's intellisense, it should work in background
  private [Symbol.toPrimitive](
    this: EnumItemClass<ET, T, K, V, LP, OPTIONS>,
    hint: 'number' | 'string' | 'default',
  ): V | string {
    if (hint === 'number') {
      // for the cases like Number(value) or +value
      return this.valueOf();
    } else if (hint === 'string') {
      // for the cases like String(value), `${value}`
      return this.toString();
    }
    // for the cases like '' + value, value == 1
    return this.valueOf();
  }

  /**
   * - **EN:** Returns the string representation of the enum item, which is its label. This method is
   *   called when the object is used in a context that requires a string value, such as string
   *   concatenation or template literals.
   *
   * > The priority of this method is lower than the `valueOf` method
   *
   * - **CN:** 返回枚举项的字符串表示形式，即其显示名称。当对象被用在需要字符串值的上下文中时会调用此方法，例如字符串连接或模板字面量。
   *
   * > 此方法的优先级低于 `valueOf` 方法
   *
   * @returns The display name of the enum item | 枚举项的显示名称
   */
  toString() {
    return this.label;
  }
  /**
   * - **EN:** Returns the localized string representation of the enum item, which is its label. This
   *   method is called when the object is used in a context that requires a localized string value,
   *   such as `toLocaleString` method.
   * - **CN:** 返回枚举项的本地化字符串表示形式，即其显示名称。当对象被用在需要本地化字符串值的上下文中时会调用此方法，例如 `toLocaleString` 方法。
   *
   * @returns The localized display name of the enum item | 枚举项的本地化显示名称
   */
  toLocaleString() {
    return this.toString();
  }
  /**
   * - **EN:** Returns the primitive value of the enum item, which is its value. This method is called
   *   when the object is used in a context that requires a primitive value, such as mathematical
   *   operations.
   *
   * > The priority of this method is higher than the `toString` method
   *
   * - **CN:** 返回枚举项的原始值，即其值。当对象被用在需要原始值的上下文中时会调用此方法，例如数学运算。
   *
   * > 此方法的优先级高于 `toString` 方法
   *
   * @returns The value of the enum item | 枚举项的值
   */
  valueOf() {
    return this.value;
  }

  private _$localize(content: string | undefined) {
    const localize = this._$options?.localize ?? localizer.localize;
    if (typeof localize === 'function') {
      return localize(content);
    }
    return content;
  }
  private _$localizeResource(resource: EnumItemLabel | undefined, field: string): string | number | undefined {
    const labelPrefix = this._$options?.labelPrefix;
    const autoLabel = this._$options?.autoLabel ?? internalConfig.autoLabel;
    const template = (this._$options?.templates?.items?.[field as never] ??
      internalConfig.templates?.items?.[field as never]) as LocalizeTemplate<'item', ET, T, K, V, LP, OPTIONS>;
    let localeKey = resource;

    if (typeof localeKey === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = localeKey(this as any);
      if (typeof result === 'string') {
        return result;
      }
      // A resource key returned by a field function takes precedence over templates,
      // just like a string returned by the function is used as the final text.
      if (result?.key != null) {
        return this._$localize(result.key) ?? result.key;
      }
    }

    if (template != null) {
      if (template === false) {
        const rawValue = this.raw?.[field as never];
        if (rawValue == null && field === 'label') {
          return this.key;
        }
        return rawValue;
      }
      localeKey = resolveLocalizeTemplate(template, {
        type: 'item',
        metaField: field,
        item: this as never,
        options: this._$options as never,
      }) as EnumItemLabel | undefined;
    } else if (field === 'label' && autoLabel && labelPrefix != null) {
      if (typeof autoLabel === 'function') {
        localeKey = autoLabel({
          item: this,
          labelPrefix: labelPrefix as LP,
        });
      } else {
        localeKey = `${labelPrefix as string}${resource}`;
      }
    }
    return this._$localize(localeKey as string | undefined) ?? localeKey;
  }
}

export interface EnumItemOptions<
  ET extends EnumInit<K, V>,
  T extends EnumItemInit<V>,
  K extends EnumKey<ET> = EnumKey<ET>,
  V extends EnumValue = ValueTypeFromSingleInit<T, K>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  LP = any,
> {
  /**
   * - **EN:** Localization function, used to convert the text of the enumeration item to localized
   *   text
   * - **CN:** 本地化函数，用于把枚举项文本转换为本地化文本
   */
  localize?: LocalizeInterface;
  /**
   * - **EN:** Internationalization templates for enum name and items, used to simplify the
   *   internationalization configuration of enums.
   * - **CN:** 枚举名称和枚举项的国际化模板，用于简化枚举的国际化配置
   */
  templates?: LocalizeTemplatesConfig<ET, T, K, V, LP, EnumItemOptions<ET, T, K, V, LP>>;
  /**
   * - **EN:** Allow setting a label prefix for enum items, simplifying or even omitting the label
   *   definition of enum items. This option is only needed when internationalization is enabled.
   *   The prefix is set through `options.labelPrefix` when creating the Enum, which can be a string
   *   or an object.
   *
   *   - `true` - Default value. Enable automatic concatenation of enum item localeKey in
   *       `options.labelPrefix` + `label` format. `labelPrefix` only supports string in this case.
   *   - `Function` - Dynamically generate the localeKey for enum items. `labelPrefix` supports any type
   *       in this case.
   *   - `false` - Disable automatic label generation, completely relying on the `label` field defined
   *       in the enum item.
   *
   * > This option has the same effect as `Enum.config.autoLabel`, but has a higher priority than the
   * > global configuration, and only takes effect for the current enum instance.
   *
   * - **CN:** 允许为枚举项设置label前缀，简化甚至可以省略枚举项的label定义，只有当开启国际化时才需要此选项。创建Enum时通过 `options.labelPrefix`
   *   设置前缀，可以是字符串，也可以是一个对象。
   *
   *   - `true` - 默认值，启用自动拼接，`options.labelPrefix` + `label` 自动拼接生成标签，这种情况下 `labelPrefix` 只支持字符串形式
   *   - `Function` - 动态生成枚举项localeKey，这种情况下 `labelPrefix` 支持任意类型
   *   - `false` - 禁用自动生成标签，完全依赖枚举项中定义的 `label` 字段
   *
   * > 此选项与 `Enum.config.autoLabel` 作用相同，但优先级高于全局配置，仅对当前枚举实例生效。
   *
   * @deprecated Use `templates.items` instead.
   */
  autoLabel?:
    | boolean
    | ((options: {
        item: EnumItemClass<ET, T, K, V, LP, EnumItemOptions<ET, T, K, V, LP>>;
        labelPrefix: LP;
      }) => string);
  /**
   * - **EN:** The label prefix for each enum item, which can be a string or an object. This option
   *   can simplify or even omit the label definition of enum items, and is only effective when
   *   internationalization is enabled.
   * - **CN:** 每个枚举项的label前缀，可以是字符串，也可以是一个对象。此选项可以简化甚至省略枚举项的label定义，只有当开启国际化时才需要此选项。
   *
   * @deprecated Use `templates.items` instead.
   */
  labelPrefix?: LP;
  /**
   * - **EN:** Set the array of meta information fields to be automatically localized, similar to the
   *   handling of `label`.
   *
   *   - `true` - Automatically localize all meta information fields.
   *   - `false` - The default value. Do not automatically localize meta information fields.
   *   - `string[]` - Specify the meta field names to be automatically localized.
   * - **CN:** 设置自动本地化的元信息字段数组，类似`label`的处理方式。
   *
   *   - `true` - 自动本地化所有元信息字段。
   *   - `false` - 默认值，不自动本地化元信息字段。
   *   - `string[]` - 指定要自动本地化的元信息字段名。
   *
   * @deprecated Use `templates.items` instead.
   */
  autoLocalizeMeta?: boolean | Exclude<keyof T, EnumItemFields>[];
}

export function metaKeys(raw: unknown) {
  return raw && typeof raw === 'object' ? Object.keys(raw) : [];
}

export function protectedKeys() {
  return ['value', 'label', 'key', 'raw'];
}
